import React, { useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { CircularProgress, Grid, Typography } from '@material-ui/core';
import jwtDecode from 'jwt-decode';

import { getCurrentUser } from '../api_handlers/auth';

const ACCENT = '#1E9CB2';

// current_user is the only place is_staff is authoritative, so it is asked
// once per page load and shared by every staff-gated route after that.
let cachedStaff = null;
let inFlight = null;

export function clearStaffCache() {
  cachedStaff = null;
  inFlight = null;
}

function fetchStaffFlag() {
  if (cachedStaff !== null) return Promise.resolve(cachedStaff);
  if (!inFlight) {
    inFlight = getCurrentUser()
      .then((response) => {
        const data = (response && response.data) || {};
        // Matches DRF's IsAdminUser, which the invoice endpoints use --
        // anything looser lands the user on a page full of 403s.
        cachedStaff = Boolean(data.is_staff);
        return cachedStaff;
      })
      .catch(() => false)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/**
 * Reports whether the signed-in user is internal staff.
 * `null` while the answer is still being fetched.
 */
export function useStaffAccess() {
  const [isStaff, setIsStaff] = useState(cachedStaff);

  useEffect(() => {
    let active = true;
    fetchStaffFlag().then((value) => {
      if (active) setIsStaff(value);
    });
    return () => {
      active = false;
    };
  }, []);

  return isStaff;
}

function hasValidToken() {
  const token = localStorage.getItem('access');
  if (!token || token === 'undefined') return false;
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp - Math.round(Date.now() / 1000) > 0;
  } catch (e) {
    return false;
  }
}

function StaffOnlyNotice() {
  return (
    <Grid container justifyContent="center" style={{ padding: '80px 16px' }}>
      <Grid item xs={12} md={6} style={{ textAlign: 'center' }}>
        <Typography style={{ fontFamily: "'Lato', sans-serif", fontWeight: 700, fontSize: 20 }}>
          Staff access only
        </Typography>
        <Typography
          style={{
            fontFamily: "'Lato', sans-serif",
            fontSize: 14,
            color: '#6B7280',
            marginTop: 8,
          }}
        >
          This page is for the internal team. If you think you should have access, ask an admin to
          mark your account as staff.
        </Typography>
      </Grid>
    </Grid>
  );
}

/**
 * A route only internal staff can open. The API enforces the same gate on
 * every endpoint behind it; this keeps the page itself out of reach so the
 * team never lands on a screen full of permission errors.
 */
export default function StaffRoute({ component: Component, ...restProps }) {
  return (
    <Route
      {...restProps}
      render={(props) => {
        if (!hasValidToken()) {
          return <Redirect to={{ pathname: '/login' }} />;
        }
        return <StaffGate {...props} {...restProps} component={Component} />;
      }}
    />
  );
}

function StaffGate({ component: Component, ...props }) {
  const isStaff = useStaffAccess();

  if (isStaff === null) {
    return (
      <Grid container justifyContent="center" style={{ padding: 80 }}>
        <CircularProgress style={{ color: ACCENT }} />
      </Grid>
    );
  }
  return isStaff ? <Component {...props} /> : <StaffOnlyNotice />;
}
